import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies - 1);
    this.#duplicateContent();

    this.#setSpeed(speed);

    // Apply or disable animation based on viewport
    this.#applyAnimationState();

    // Always listen for resize to handle viewport changes and recalculate
    window.addEventListener('resize', this.#handleResize);

    // Only add hover slow-down when animation is enabled and interaction is allowed
    if (!this.#isAnimationDisabled() && !this.hasAttribute('data-prevent-touch-reset')) {
      this.addEventListener('pointerenter', this.#slowDown);
      this.addEventListener('pointerleave', this.#speedUp);
    }

    if (this.hasAttribute('data-prevent-touch-reset')) {
      this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
      this.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true });
    }
  }

  /**
   * Apply animation state based on viewport and data attributes
   */
  #applyAnimationState() {
    const shouldDisable = this.#isAnimationDisabled();
    const wasDisabled = this.hasAttribute('data-animation-disabled');
    const { wrapper } = this.refs;

    if (shouldDisable) {
      this.setAttribute('data-animation-disabled', '');
      // Use inline style to ensure animation is disabled (bypasses CSS scoping)
      wrapper.style.animation = 'none';
      // Remove hover interaction when animation is disabled
      this.removeEventListener('pointerenter', this.#slowDown);
      this.removeEventListener('pointerleave', this.#speedUp);
    } else {
      this.removeAttribute('data-animation-disabled');

      // Force animation restart if transitioning from disabled to enabled
      if (wasDisabled) {
        // Clear inline style and force reflow to restart CSS animation
        wrapper.style.animation = 'none';
        void wrapper.offsetWidth;
        wrapper.style.animation = '';
        if (!this.hasAttribute('data-prevent-touch-reset')) {
          this.addEventListener('pointerenter', this.#slowDown);
          this.addEventListener('pointerleave', this.#speedUp);
        }
      }
    }
  }

  /**
   * Check if animation should be disabled based on data attributes and viewport
   * @returns {boolean}
   */
  #isAnimationDisabled() {
    const hasDisabledDesktop = this.hasAttribute('data-disabled-desktop');
    const hasDisabledMobile = this.hasAttribute('data-disabled-mobile');
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    // Check prefers-reduced-motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return true;
    }

    // On desktop: disabled if data-disabled-desktop is set
    if (isDesktop && hasDisabledDesktop) {
      return true;
    }

    // On mobile: disabled if data-disabled-mobile is set
    if (!isDesktop && hasDisabledMobile) {
      return true;
    }

    return false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchend', this.#handleTouchEnd);
    this.removeEventListener('touchcancel', this.#handleTouchEnd);
    this.#scheduleRecalcAfterTouchEnd.cancel();
  }

  #handleTouchStart = () => {
    this.#scheduleRecalcAfterTouchEnd.cancel();
    this.#touchActive = true;
  };

  #handleTouchEnd = () => {
    this.#scheduleRecalcAfterTouchEnd();
  };

  #scheduleRecalcAfterTouchEnd = debounce(() => {
    this.#touchActive = false;
    if (this.hasAttribute('data-prevent-touch-reset')) {
      this.#runResizeRecalc();
    }
  }, 300);

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  /**
   * @type {boolean}
   */
  #touchActive = false;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * Get current number of marquee__repeated-items from DOM
   * (refs don't update after clones are added)
   * @returns {number}
   */
  #getCurrentCopyCount() {
    const { content } = this.refs;
    return content.querySelectorAll('.marquee__repeated-items').length;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  async #runResizeRecalc() {
    this.#applyAnimationState();

    const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    if (!isHorizontalResize) return;

    const currentNumberOfCopies = this.#getCurrentCopyCount();
    const speed = this.#calculateSpeed(numberOfCopies);

    if (numberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(numberOfCopies - currentNumberOfCopies);
    } else if (numberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }

  #handleResize = debounce(async () => {
    if (this.hasAttribute('data-prevent-touch-reset') && this.#touchActive) return;
    await this.#runResizeRecalc();
  }, ANIMATION_OPTIONS.duration);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);

    // Apply z-index after content is duplicated
    this.#applyZIndex();
  }

  /**
   * Apply continuous z-index to all marquee items across all groups
   * First item gets highest z-index, last item gets lowest
   */
  #applyZIndex() {
    const { wrapper } = this.refs;
    const allItems = wrapper.querySelectorAll('.marquee__repeated-items > *');
    const totalItems = allItems.length;

    allItems.forEach((item, index) => {
      // First item gets highest z-index, decreasing from there
      /** @type {HTMLElement} */ (item).style.zIndex = String(totalItems - index);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  /**
   * Add exact number of marquee item clones
   * @param {number} count - Exact number of clones to add
   */
  #addRepeatedItems(count) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0] || count <= 0) return;

    for (let i = 0; i < count; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
